import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Button, Text, TextInput, Textarea } from '@tremor/react'
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function GoalFormModal({ isOpen, onClose, onSubmit, editGoal = null, loading = false }) {
    const { t } = useTranslation()
    const isEditing = !!editGoal
    
    const [formData, setFormData] = useState(() => ({
        general_goal: editGoal?.general_goal || '',
        specific_goals: editGoal?.specific_goals || [''],
        reasoning: editGoal?.reasoning || '',
        scope: editGoal?.scope || 'academic',
        status: editGoal?.status || 'draft'
    }))
    const [error, setError] = useState(null)

    // Update form data when editGoal prop changes
    useEffect(() => {
        if (editGoal) {
            setFormData({
                general_goal: editGoal.general_goal || '',
                specific_goals: editGoal.specific_goals && editGoal.specific_goals.length > 0 ? editGoal.specific_goals : [''],
                reasoning: editGoal.reasoning || '',
                scope: editGoal.scope || 'academic',
                status: editGoal.status || 'draft'
            })
        } else {
            // Reset form for create mode
            setFormData({
                general_goal: '',
                specific_goals: [''],
                reasoning: '',
                scope: 'academic',
                status: 'draft'
            })
        }
        setError(null)
    }, [editGoal])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: sanitizeUserInput(value)
        }))
    }

    const handleSpecificGoalChange = (index, value) => {
        const sanitized = sanitizeUserInput(value)
        setFormData(prev => ({
            ...prev,
            specific_goals: prev.specific_goals.map((goal, i) => 
                i === index ? sanitized : goal
            )
        }))
    }

    const addSpecificGoal = () => {
        setFormData(prev => ({
            ...prev,
            specific_goals: [...prev.specific_goals, '']
        }))
    }

    const removeSpecificGoal = (index) => {
        if (formData.specific_goals.length > 1) {
            setFormData(prev => ({
                ...prev,
                specific_goals: prev.specific_goals.filter((_, i) => i !== index)
            }))
        }
    }

    const validateForm = () => {
        if (!formData.general_goal.trim()) {
            setError(t('Research.ObjectivosHipoteses.Goals.Manual.GeneralGoalRequired'))
            return false
        }

        const validSpecificGoals = formData.specific_goals.filter(goal => goal.trim())
        if (validSpecificGoals.length === 0) {
            setError(t('Research.ObjectivosHipoteses.Goals.Manual.SpecificGoalsRequired'))
            return false
        }

        setError(null)
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) return

        const submitData = {
            ...formData,
            specific_goals: formData.specific_goals.filter(goal => goal.trim()),
            type: 'manual'
        }

        try {
            await onSubmit(submitData)
            handleClose()
        } catch (err) {
            setError(err.error || t('Research.ObjectivosHipoteses.Goals.Manual.SubmitError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData({
                general_goal: '',
                specific_goals: [''],
                reasoning: '',
                scope: 'academic',
                status: 'draft'
            })
            setError(null)
            onClose()
        }
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {isEditing 
                                            ? t('Research.ObjectivosHipoteses.Goals.Manual.EditTitle')
                                            : t('Research.ObjectivosHipoteses.Goals.Manual.CreateTitle')
                                        }
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        disabled={loading}
                                        className="rounded-md p-2 hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* General Goal */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.ObjectivosHipoteses.Goals.Manual.GeneralGoalLabel')} *
                                        </label>
                                        <Textarea
                                            placeholder={t('Research.ObjectivosHipoteses.Goals.Manual.GeneralGoalPlaceholder')}
                                            value={formData.general_goal}
                                            onChange={(e) => handleInputChange('general_goal', e.target.value)}
                                            rows={3}
                                            disabled={loading}
                                            className="w-full"
                                        />
                                    </div>

                                    {/* Specific Goals */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {t('Research.ObjectivosHipoteses.Goals.Manual.SpecificGoalsLabel')} *
                                            </label>
                                            <Button
                                                type="button"
                                                size="sm"
                                                icon={PlusIcon}
                                                onClick={addSpecificGoal}
                                                disabled={loading}
                                                className="bg-iavisus-100 text-white hover:bg-iavisus-200 text-sm"
                                            >
                                                {t('Research.ObjectivosHipoteses.Goals.Manual.AddSpecificGoal')}
                                            </Button>
                                        </div>
                                        <div className="space-y-2">
                                            {formData.specific_goals.map((goal, index) => (
                                                <div key={index} className="flex gap-2">
                                                    <TextInput
                                                        placeholder={t('Research.ObjectivosHipoteses.Goals.Manual.SpecificGoalPlaceholder')}
                                                        value={goal}
                                                        onChange={(e) => handleSpecificGoalChange(index, e.target.value)}
                                                        disabled={loading}
                                                        className="flex-1"
                                                    />
                                                    {formData.specific_goals.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeSpecificGoal(index)}
                                                            disabled={loading}
                                                            className="p-2 bg-red-600 text-white hover:bg-red-700 rounded-md disabled:opacity-50"
                                                            title={t('Research.ObjectivosHipoteses.Goals.Manual.RemoveSpecificGoal')}
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Scope */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.ObjectivosHipoteses.Goals.Manual.ScopeLabel')}
                                        </label>
                                        <select
                                            value={formData.scope}
                                            onChange={(e) => handleInputChange('scope', e.target.value)}
                                            disabled={loading}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                        >
                                            <option value="academic">{t('Research.ObjectivosHipoteses.Scopes.Academic')}</option>
                                            <option value="business">{t('Research.ObjectivosHipoteses.Scopes.Business')}</option>
                                            <option value="mixed">{t('Research.ObjectivosHipoteses.Scopes.Mixed')}</option>
                                        </select>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.ObjectivosHipoteses.Goals.Manual.StatusLabel')}
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => handleInputChange('status', e.target.value)}
                                            disabled={loading}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                                        >
                                            <option value="draft">{t('Research.ObjectivosHipoteses.Goals.Status.draft')}</option>
                                            <option value="active">{t('Research.ObjectivosHipoteses.Goals.Status.active')}</option>
                                            <option value="archived">{t('Research.ObjectivosHipoteses.Goals.Status.archived')}</option>
                                        </select>
                                    </div>

                                    {/* Reasoning (Optional) */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.ObjectivosHipoteses.Goals.Manual.ReasoningLabel')} ({t('Common.Optional')})
                                        </label>
                                        <Textarea
                                            placeholder={t('Research.ObjectivosHipoteses.Goals.Manual.ReasoningPlaceholder')}
                                            value={formData.reasoning}
                                            onChange={(e) => handleInputChange('reasoning', e.target.value)}
                                            rows={3}
                                            disabled={loading}
                                            className="w-full"
                                        />
                                    </div>

                                    {/* Error Message */}
                                    {error && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <Text className="text-red-600 text-sm">{error}</Text>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            type="button"
                                            onClick={handleClose}
                                            disabled={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {t('Common.Cancel')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            loading={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {isEditing ? t('Common.Save') : t('Common.Create')}
                                        </Button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default GoalFormModal